import React, { useState, useEffect, useMemo, useRef } from "react";
import { getWorkflowSteps } from "../../Utils/literatureReviewWorkflow";
import LiteratureReviewLayout from "../../Components/LiteratureReviewLayout/LiteratureReviewLayout";
import EvaluateResults from "../../Components/EvaluateResults/EvaluateResults";
import { DoiButton } from "../../Components/DoiButton/DoiButton";
import FileSelector from "../../Components/FileSelector/FileSelector";
import { Search, Info, Close, UploadFile } from "@mui/icons-material";
import { Button } from "@mui/material";
import { getPdfFileUrls, startFullTextScreening, getLiteratureReview } from "../../Utils/functions";
import "./FullTextScreening.css";
import LinearProgress from "@mui/material/LinearProgress";
import { useLiteratureReview } from "../../Context/LiteratureReviewContext";

const FullTextScreening = () => {
    const { id, workflowData, setWorkflowData, navigateToStep, setFromStep, getStepsForPage } = useLiteratureReview();

    const steps = getStepsForPage("full-text-screening");

    const screenedArticles = useMemo(() => workflowData.screenedArticles ?? [], [workflowData.screenedArticles]);
    const articleUniqueIds = useMemo(() => workflowData.articleUniqueIds ?? [], [workflowData.articleUniqueIds]);
    const initialValues = useMemo(() => workflowData.articleValues ?? {}, [workflowData.articleValues]);
    const suggestedColumns = useMemo(() => workflowData.suggestedColumns ?? [], [workflowData.suggestedColumns]);

    const [articles, setArticles] = useState([]);
    const [evaluating, setEvaluating] = useState(false);
    const [evaluationComplete, setEvaluationComplete] = useState(false);
    const [progress, setProgress] = useState(0);
    const intervalRef = useRef(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    const [isFileSelectorOpen, setIsFileSelectorOpen] = useState(false);
    const [currentArticleIdForUpload, setCurrentArticleIdForUpload] = useState(null);

    const getValueByArticleId = (obj, uniqueId) => {
        if (!obj || uniqueId == null) return undefined;
        return obj[uniqueId] ?? obj[String(uniqueId)] ?? undefined;
    };

    const normalizeStatus = (rawStatus) => {
        if (!rawStatus) return "pending";
        const status = String(rawStatus).toLowerCase();
        if (["done", "completed", "complete", "success", "succeeded", "finished"].includes(status)) return "done";
        if (["failed", "error", "errored"].includes(status)) return "failed";
        if (["processing", "running", "in_progress", "in-progress"].includes(status)) return "processing";
        return "pending";
    };

    const normalizeEvaluation = (evaluation) => {
        if (!evaluation || typeof evaluation !== "object") return null;
        const normalized = { ...evaluation };

        // EvaluateResults reads inclusion_scores, while full-text API may send inclusionScore.
        if (normalized.inclusionScore && !normalized.inclusion_scores) {
            normalized.inclusion_scores = normalized.inclusionScore;
        }

        return normalized;
    };

    const fetchFullTextUrls = async (targetArticles) => {
        const doisToFetch = targetArticles.map(a => a.doi).filter(Boolean);
        if (doisToFetch.length === 0) return;

        try {
            const data = await getPdfFileUrls(doisToFetch);

            if (data?.results) {
                setArticles(prev => prev.map(article => {
                    const match = data.results.find(res => res.DOI === article.doi);
                    if (match) {
                        return {
                            ...article,
                            fileUrl: match.fileUrl || null,
                            fileMissing: !match.available || !match.fileUrl
                        };
                    }
                    return article;
                }));
            }
        } catch (error) {
            console.error("Error fetching PDF URLs:", error);
        }
    };

    // Initialize articles state with necessary metadata and fetch available full texts.
    useEffect(() => {
        const initialArticles = articleUniqueIds.map((aid) => {
            const paper = screenedArticles.find((s) => String(s?.uniqueId) === String(aid)) || {};
            const doi = paper.doi || "";
            return {
                uniqueId: aid,
                paper,
                doi,
                fileUrl: null,
                fileMissing: false,
                status: "pending",
                evaluation: null
            };
        });

        setArticles(initialArticles);

        if (initialArticles.length > 0) {
            fetchFullTextUrls(initialArticles);
        }
    }, [articleUniqueIds, screenedArticles]);

    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    const startPolling = (submittedIds) => {
        if (intervalRef.current) clearInterval(intervalRef.current);

        const poll = async () => {
            try {
                const data = await getLiteratureReview(id);
                const rawStatusMap = data.fullTextArticleStatus;
                const rawValuesMap = data.fullTextArticleValues;

                const normalizedStatusMap = {};
                submittedIds.forEach((uid) => {
                    const rawStatus = getValueByArticleId(rawStatusMap, uid);
                    if (rawStatus) {
                        normalizedStatusMap[uid] = normalizeStatus(rawStatus);
                        return;
                    }

                    const hasValue = !!getValueByArticleId(rawValuesMap, uid);
                    if (hasValue) {
                        normalizedStatusMap[uid] = "done";
                    }
                });

                const totalArticles = submittedIds.length;
                const terminalCount = submittedIds.filter((uid) => {
                    const status = normalizedStatusMap[uid];
                    return status === "done" || status === "failed";
                }).length;

                const allFinished =
                    totalArticles > 0 &&
                    submittedIds.every((uid) => {
                        const status = normalizedStatusMap[uid];
                        return status === "done" || status === "failed";
                    });

                setArticles((prev) =>
                    prev.map((article) => {
                        const mappedStatus = normalizedStatusMap[article.uniqueId] || article.status;
                        const mappedEvaluation = normalizeEvaluation(
                            getValueByArticleId(rawValuesMap, article.uniqueId) || article.evaluation
                        );
                        return {
                            ...article,
                            status: mappedStatus,
                            evaluation: mappedEvaluation,
                        };
                    })
                );

                setProgress(totalArticles > 0 ? (terminalCount / totalArticles) * 100 : 0);

                if (allFinished) {
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    setEvaluating(false);
                    setEvaluationComplete(true);
                }
            } catch (error) {
                console.error("Polling error:", error);
            }
        };

        poll();
        intervalRef.current = setInterval(poll, 5000);
    };

    const handleEvaluate = async () => {
        if (evaluating) return;

        const articlesPayload = articles
            .filter((a) => a.fileUrl)
            .map((a) => ({
                uniqueArticleId: a.uniqueId,
                DOI: a.doi || "",
                fileURL: a.fileUrl,
            }));
        const submittedIds = articlesPayload.map((a) => a.uniqueArticleId);
        if (submittedIds.length === 0) return;

        try {
            setEvaluating(true);
            setProgress(0);
            setArticles((prev) =>
                prev.map((article) =>
                    submittedIds.includes(article.uniqueId)
                        ? { ...article, status: "processing" }
                        : article
                )
            );
            await startFullTextScreening(id, articlesPayload);
            startPolling(submittedIds);
        } catch (error) {
            console.error("Error starting evaluation:", error);
            setEvaluating(false);
        }
    };

    const handleBack = () => {
        navigateToStep("recommended-abstracts");
    };

    // Handle selection from FileSelector for a specific article
    const handleFileSelect = (file, isFileSelected = false) => {
        if (!currentArticleIdForUpload) return;

        // If file is unselected, we do nothing for now (or maybe clear it)
        if (isFileSelected) return;

        // Update the article with the selected file's ID / URL
        const url = file.fileKey || file.id || file.url;
        if (url) {
            setArticles(prev => prev.map(a =>
                a.uniqueId === currentArticleIdForUpload ? { ...a, fileUrl: url, fileMissing: false } : a
            ));
        }

        setIsFileSelectorOpen(false);
        setCurrentArticleIdForUpload(null);
    };

    const displayedArticles = useMemo(() => {
        if (!searchQuery.trim()) return articles;
        const q = searchQuery.trim().toLowerCase();
        return articles.filter((a) => {
            const title = (a.paper.title || "").toLowerCase();
            const rawAuthors = a.paper.authors;
            const authors = Array.isArray(rawAuthors) ? rawAuthors.join(", ").toLowerCase() : (rawAuthors || "").toLowerCase();
            const abstract = (a.paper.abstract || "").toLowerCase();
            return title.includes(q) || authors.includes(q) || abstract.includes(q);
        });
    }, [articles, searchQuery]);

    const hasMissingFiles = articles.some(a => a.fileMissing || !a.fileUrl);
    const hasReadyFiles = articles.some((a) => Boolean(a.fileUrl));
    const canEvaluate = articles.length > 0 && hasReadyFiles && !evaluating;

    // Build articleValues map from completed evaluations for EvaluateResults
    const completedArticleValues = useMemo(() => {
        const vals = {};
        articles.forEach(a => {
            if (a.status === "done" && a.evaluation) {
                vals[a.uniqueId] = normalizeEvaluation(a.evaluation);
            }
        });
        console.log("Completed Article Values for Results:", vals);
        return vals;
    }, [articles]);

    const completedArticleIds = useMemo(() =>
        articles.filter(a => a.status === "done").map(a => a.uniqueId),
        [articles]
    );

    const completedScreenedArticles = useMemo(() =>
        completedArticleIds.map(uid => screenedArticles.find(s => String(s?.uniqueId) === String(uid))).filter(Boolean),
        [completedArticleIds, screenedArticles]
    );

    const handleResultsNext = (includedIds, includedScreenedArticles, includedArticleValues) => {
        // Articles that never completed full-text evaluation (no PDF / failed) still count as
        // "included on the recommended-abstracts page" and should use their abstracts.
        // Articles that completed FTS but were excluded are dropped entirely.
        const noFullTextIds = articleUniqueIds.filter(uid => !completedArticleIds.includes(uid));
        const noFullTextScreenedArticles = noFullTextIds
            .map(uid => screenedArticles.find(s => String(s?.uniqueId) === String(uid)))
            .filter(Boolean);
        const noFullTextArticleValues = {};
        noFullTextIds.forEach(uid => {
            const raw = initialValues[uid];
            if (raw) noFullTextArticleValues[uid] = raw;
        });

        setWorkflowData({
            articleUniqueIds: [...includedIds, ...noFullTextIds],
            screenedArticles: [...includedScreenedArticles, ...noFullTextScreenedArticles],
            articleValues: { ...noFullTextArticleValues, ...includedArticleValues },
            fullTextArticleIds: includedIds,
            fullTextUploadedCount: completedArticleIds.length,
        });
        setFromStep("full-text-screening");
        navigateToStep("data-extraction-definition");
    };

    const handleResultsBack = () => {
        setEvaluationComplete(false);
    };

    const mainContent = (
        <div className="eval-screening-content fts-content">
            <div className="fts-toolbar-actions eval-screening-toolbar">
                {isSearchOpen ? (
                    <div className="eval-screening-search-wrapper">
                        <Search sx={{ fontSize: 16, color: "#666" }} />
                        <input
                            type="text"
                            className="eval-screening-search-input"
                            placeholder="Search title, author..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                        />
                        <button
                            className="eval-screening-search-close"
                            onClick={() => { setIsSearchOpen(false); setSearchQuery(""); }}
                        >
                            <Close sx={{ fontSize: 16 }} />
                        </button>
                    </div>
                ) : (
                    <button className="eval-screening-toolbar-btn" onClick={() => setIsSearchOpen(true)}>
                        <Search sx={{ fontSize: 16 }} /> Search
                    </button>
                )}
            </div>


            <div className="eval-screening-table-wrapper fts-table-wrapper">
                <table className="eval-screening-table fts-table">
                    <thead>
                        <tr>
                            <th className="eval-screening-paper-col">Paper</th>
                            <th className="fts-file-col">File Status</th>
                            <th className="fts-evaluation-col">Evaluation Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedArticles.map((article) => {
                            const { uniqueId, paper, fileUrl, fileMissing, status, evaluation } = article;
                            const title = paper?.title || "Unknown Title";
                            const rawAuthors = paper?.authors;
                            const authorsList = Array.isArray(rawAuthors) ? rawAuthors : [];
                            const authors = authorsList.length > 0 ? authorsList.join(", ") : (rawAuthors || "");

                            return (
                                <tr key={uniqueId} className="eval-screening-row fts-row">
                                    <td className="eval-screening-paper-cell">
                                        <div className="eval-screening-paper-card">
                                            <div className="eval-screening-paper-title">{title}</div>
                                            {authors && <div className="eval-screening-paper-authors">{authors}</div>}
                                            <div className="eval-screening-paper-meta">
                                                {paper?.doi && <DoiButton doi={paper.doi} />}
                                            </div>
                                        </div>
                                    </td>

                                    <td className="fts-file-cell">
                                        {fileUrl ? (
                                            <div className="fts-status-pill fts-success">
                                                <span className="fts-dot success-dot" /> File ready
                                            </div>
                                        ) : fileMissing ? (
                                            <div className="fts-missing-wrapper">
                                                <div className="fts-status-pill fts-info">
                                                    <Info sx={{ fontSize: 14, mr: 0.5, flexShrink: 0 }} /> Full text not available. Please add the full text to continue.
                                                </div>
                                                <div className="fts-upload-mini">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<UploadFile />}
                                                        onClick={() => {
                                                            setCurrentArticleIdForUpload(uniqueId);
                                                            setIsFileSelectorOpen(true);
                                                        }}
                                                    >
                                                        Upload Full-text File
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="fts-status-pill fts-pending">
                                                Pending lookup...
                                            </div>
                                        )}
                                    </td>

                                    <td className="fts-evaluation-cell">
                                        <div className={`fts-status-pill fts-eval-${status}`}>
                                            {status === "processing" ? "Processing..." :
                                                status === "done" ? "Done" :
                                                    status === "failed" ? "Failed" : "Pending"}
                                        </div>
                                        {evaluation?.inclusionScore && (
                                            <div className="fts-eval-score">
                                                Score: {evaluation.inclusionScore.value} / 10
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {displayedArticles.length === 0 && (
                            <tr>
                                <td colSpan="3" style={{ textAlign: "center", padding: "20px" }}>
                                    No articles found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const sidebar = (
        <div className="eval-screening-sidebar">
            <div className="eval-screening-sidebar-header">
                <h3 className="eval-screening-sidebar-title">Actions</h3>
            </div>

            <div className="eval-screening-sidebar-section">
                <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
                    Full texts are retrieved automatically when this page loads. Upload any missing files before evaluating.
                </p>
                <button
                    className={`fts-evaluate-btn ${!canEvaluate ? "disabled" : ""}`}
                    onClick={handleEvaluate}
                    disabled={!canEvaluate}
                >
                    {evaluating ? "Evaluating..." : "Evaluate full-text screening"}
                </button>
                {evaluating && (
                    <div style={{ marginTop: "16px" }}>
                        <LinearProgress variant="determinate" value={progress} />
                        <p style={{ fontSize: "12px", color: "#64748b", marginTop: "8px", textAlign: "center" }}>
                            {Math.round(progress)}% Completed
                        </p>
                    </div>
                )}
            </div>

            <div className="eval-screening-sidebar-section">
                <h4 className="eval-screening-sidebar-subtitle">Overview</h4>
                <ul className="eval-screening-overview-list" style={{ listStyle: "none", padding: 0, margin: 0, fontSize: "13px" }}>
                    <li style={{ padding: "4px 0", borderBottom: "1px solid #f1f5f9" }}>
                        <strong>{articles.length}</strong> Total Articles
                    </li>
                    <li style={{ padding: "4px 0", borderBottom: "1px solid #f1f5f9", color: hasMissingFiles ? "#d97706" : "inherit" }}>
                        <strong>{articles.filter(a => a.fileUrl).length}</strong> Ready Files
                    </li>
                    <li style={{ padding: "4px 0", borderBottom: "1px solid #f1f5f9" }}>
                        <strong>{articles.filter(a => a.status === "done").length}</strong> Evaluated
                    </li>
                </ul>
            </div>
        </div>
    );
    if (evaluationComplete) {
        console.log("Evaluation complete. Articles:");
        console.log("Completed Article Values for Results:", completedArticleValues);
    }

    return (
        <>
            {evaluationComplete && Object.keys(completedArticleValues).length > 0 ? (
                <EvaluateResults
                    projectTitle={workflowData.litreviewtitle}
                    screenedArticles={completedScreenedArticles}
                    articleUniqueIds={completedArticleIds}
                    articleValues={completedArticleValues}
                    suggestedColumns={suggestedColumns}
                    screeningType="full-text"
                    steps={getWorkflowSteps("full-text-screening")}
                    onBack={handleResultsBack}
                    onNext={handleResultsNext}
                    nextLabel="Data Extraction"
                    showBack={true}
                />
            ) : (
                <>
                    <LiteratureReviewLayout
                        projectTitle={workflowData.litreviewtitle || "Full text screening"}
                        editableTitle={!!id}
                        steps={steps}
                        mainContent={mainContent}
                        sidebar={sidebar}
                        onBack={handleBack}
                        showBack={true}
                        showNext={false}
                    />
                    <FileSelector
                        isOpen={isFileSelectorOpen}
                        onClose={() => {
                            setIsFileSelectorOpen(false);
                            setCurrentArticleIdForUpload(null);
                        }}
                        onFileSelect={handleFileSelect}
                        selectedFiles={[]}
                    />
                </>
            )}
        </>
    );
};

export default FullTextScreening;
